/**
 * Memoizes per-day weather statistics for the water-potential graph.
 *
 * `FieldDetailsWPGraph` recomputed `weatherDataStatistics` for the field's
 * hourly temperature and ETc series on every WP sensor response and on every
 * history reload a field click triggers — each a year+ of samples on the main
 * thread. The reloads hand over a *new* `values` object with the same content,
 * so the cache keys on content, not identity.
 *
 * Key: timezone, sample count, first/last timestamp and the sum of values.
 * Computing it is a single pass (~1ms for 14k samples) versus ~175ms for the
 * statistics. A reload with new samples changes count/last key; a revised
 * value changes the sum.
 *
 * Pure (the compute function is injected) so it runs under node:test.
 */

// A few entries: temperature + ETc for the current field and the previous one.
const MAX_ENTRIES = 8;
const cache = new Map();

const fingerprint = (values, timezone) => {
  const keys = Object.keys(values);
  let sum = 0;
  let firstKey;
  let lastKey;
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    if (firstKey === undefined || key < firstKey) firstKey = key;
    if (lastKey === undefined || key > lastKey) lastKey = key;
    const value = Number(values[key]);
    sum += Number.isNaN(value) ? 0 : value;
  }
  return `${timezone}|${keys.length}|${firstKey}|${lastKey}|${sum}`;
};

/**
 * @param {Function} computeStatistics - weatherManager.weatherDataStatistics
 * @param {Object|null|undefined} values - timestamp -> value series
 * @param {string|undefined} timezone
 * @returns {Object} per-day statistics
 */
const cachedWeatherDataStatistics = (computeStatistics, values, timezone) => {
  if (!values || typeof values !== 'object') {
    return computeStatistics(values, timezone);
  }

  const key = fingerprint(values, timezone);
  if (cache.has(key)) {
    const result = cache.get(key);
    // Refresh recency.
    cache.delete(key);
    cache.set(key, result);
    return result;
  }

  const result = computeStatistics(values, timezone);
  cache.set(key, result);
  if (cache.size > MAX_ENTRIES) {
    cache.delete(cache.keys().next().value);
  }
  return result;
};

module.exports = { cachedWeatherDataStatistics, MAX_ENTRIES };
